/**
 * <khaime-storefront> Web Component
 * Main product grid/list component with filtering and pagination
 */

import { KhaimeBaseElement, BASE_STYLES, ComponentRegistry } from '../base';
import { STOREFRONT_STYLES } from './styles';
import { KhaimeProductCardElement } from './khaime-product-card';
import type { Product, ProductVariant, BootstrapResponse } from '../../types';
import { createKhaimeEvent } from '../../types/events';
import { escapeHtml } from '../../utils';
import { CartManager } from '../../utils/cart-manager';
import { createCartIcon } from '../cart/cart-icon';
import { mountCustomSelect } from '../shared/custom-select';
import { flagUrl } from '../shared/flag';

export class KhaimeStorefrontElement extends KhaimeBaseElement {
  static tagName = 'khaime-storefront';

  private _products: Product[] = [];
  private _bootstrap: BootstrapResponse | null = null;
  private _currency: string = 'USD';
  private _selectedCategory: string = 'All';
  private _currentPage: number = 1;
  private _totalProducts: number = 0;
  private _limit: number = 20;
  private _hasMore: boolean = false;
  private _loadingMore: boolean = false;
  private _cartManager: CartManager | null = null;
  private _cartIconDestroy: (() => void) | null = null;
  private _detailPanelCleanup: (() => void) | null = null;
  private _drawerCleanup: (() => void) | null = null;

  static get observedAttributes(): string[] {
    return [
      ...super.observedAttributes,
      'product-ids',
      'category',
      'category-id',
      'category-slug',
      'type',
      'sort',
      'limit',
      'show-categories',
      'show-currency',
      'show-pagination',
      'title',
      'cart-display',
      'cart-url',
      'enabled-currencies',
    ];
  }

  /**
   * Parse the merchant-configured currency whitelist from the
   * `enabled-currencies` attribute (comma-separated ISO codes). Empty
   * means no whitelist — show everything the account supports.
   */
  private get enabledCurrencies(): string[] {
    const raw = this.getAttribute('enabled-currencies') || '';
    return raw
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter((c) => /^[A-Z]{3}$/.test(c));
  }

  private get cartDisplay(): 'drawer' | 'page' | 'both' {
    const v = this.getAttribute('cart-display');
    return v === 'page' || v === 'both' ? v : 'drawer';
  }

  private get cartUrl(): string {
    return this.getAttribute('cart-url') || '/cart';
  }

  /**
   * Build the product query filter from the component's current attributes.
   * Used by loadData / loadMore / refreshProducts so they share one source
   * of truth for which filters get forwarded to the API. Adding a new
   * filter? Add it here only.
   */
  private buildProductsQuery(extra: { limit: number; offset: number }): {
    currency: string;
    productIds?: string;
    category?: string;
    categoryId?: string;
    categorySlug?: string;
    type?: any;
    sort?: any;
    limit: number;
    offset: number;
  } {
    return {
      currency: this._currency,
      productIds: this.getAttribute('product-ids') || undefined,
      category: this.getAttribute('category') || undefined,
      categoryId: this.getAttribute('category-id') || undefined,
      categorySlug: this.getAttribute('category-slug') || undefined,
      type: (this.getAttribute('type') as any) || undefined,
      sort: (this.getAttribute('sort') as any) || undefined,
      limit: extra.limit,
      offset: extra.offset,
    };
  }

  /**
   * Get SDK type
   */
  protected getSDKType(): string {
    return 'storefront';
  }

  /**
   * Render the storefront
   */
  protected async render(): Promise<void> {
    // Inject component-specific styles
    this.injectStyles(STOREFRONT_STYLES);

    // Remove any existing loading container from base class
    const existingLoader = this.shadow.querySelector('.ks-container');
    if (existingLoader) existingLoader.remove();

    // Render shell with skeleton grid
    const columns = parseInt(this.getAttribute('columns') || '4', 10);
    const limit = parseInt(this.getAttribute('limit') || '8', 10);
    const skeletonCount = Math.min(limit, 8);

    const container = document.createElement('div');
    container.className = 'ks-storefront ks-container';
    container.setAttribute('part', 'container');
    container.innerHTML = `
        <header class="ks-header" part="header">
          <slot name="title">
            <h2 class="ks-title" part="title">${escapeHtml(this.getAttribute('title') || 'Products')}</h2>
          </slot>
          <div class="ks-controls" part="controls">
            ${this.shouldShowCurrency() ? '<div class="ks-currency-select" part="currency-select"></div>' : ''}
            ${this.hasAttribute('type') ? '' : '<div class="ks-type-filter" part="type-filter"></div>'}
          </div>
        </header>
        ${this.shouldShowCategories() ? '<div class="ks-categories" part="categories"></div>' : ''}
        <div class="ks-grid" part="grid" style="--ks-columns: ${columns}">
          ${this.renderSkeletonCards(skeletonCount)}
        </div>
        <div class="ks-empty" part="empty" hidden>No products found</div>
        ${this.shouldShowPagination() ? '<div class="ks-pagination" part="pagination"></div>' : ''}
        <slot name="footer"></slot>
    `;
    this.shadow.appendChild(container);

    // Load data (replaces skeleton with real products)
    await this.loadData();
  }

  /**
   * Render skeleton placeholder cards for loading state
   */
  private renderSkeletonCards(count: number): string {
    const card = `
      <div class="ks-skeleton-card">
        <div class="ks-skeleton ks-skeleton-card__image"></div>
        <div class="ks-skeleton-card__body">
          <div class="ks-skeleton ks-skeleton-text" style="width: 40%"></div>
          <div class="ks-skeleton ks-skeleton-text--lg" style="width: 85%"></div>
          <div class="ks-skeleton ks-skeleton-text" style="width: 50%"></div>
          <div class="ks-skeleton ks-skeleton-text--sm" style="width: 30%"></div>
        </div>
      </div>
    `;
    return card.repeat(count);
  }

  /**
   * Load bootstrap and products data
   */
  private async loadData(): Promise<void> {
    if (!this.api) return;

    try {
      // Parallel fetch: bootstrap + products
      const [bootstrap, productsResponse] = await Promise.all([
        this.api.bootstrap(),
        this.api.getProducts(
          this.buildProductsQuery({
            limit: parseInt(this.getAttribute('limit') || String(this._limit), 10),
            offset: (this._currentPage - 1) * this._limit,
          })
        ),
      ]);

      this._bootstrap = bootstrap;
      this._products = productsResponse.products;
      this._totalProducts = productsResponse.total;
      this._hasMore = productsResponse.hasMore;

      // Set detected currency
      if (bootstrap.detectedCurrency) {
        this._currency = bootstrap.detectedCurrency;
      }

      // Initialize cart manager
      if (this.api && !this._cartManager) {
        this._cartManager = CartManager.getInstance(this.merchantId, this.api);
        this._cartManager.setCurrency(this._currency);
        this._cartManager.hydrate().catch(() => {});
      }

      // Render UI elements
      this.renderCurrencySelect();
      this.renderTypeFilter();
      this.renderCategories();
      this.renderProducts();
      this.renderPagination();
      this.renderCartIcon();

    } catch (error) {
      console.error('[Khaime Storefront]', error);
      this.showEmpty();
    }
  }

  /**
   * Render currency selector
   */
  private renderCurrencySelect(): void {
    const host = this.shadow.querySelector('.ks-currency-select') as HTMLElement;
    if (!host || !this._bootstrap) return;

    // Apply the merchant's enabled-currencies whitelist. An empty whitelist
    // (the default) means: show all currencies the account supports. The
    // active currency is always retained so the UI can reflect its own state.
    const whitelist = this.enabledCurrencies;
    const visible = whitelist.length
      ? this._bootstrap.currencies.filter(
          (c) => whitelist.includes(c.code.toUpperCase()) || c.code === this._currency
        )
      : this._bootstrap.currencies;

    mountCustomSelect(host, {
      options: visible.map((c) => ({
        value: c.code,
        label: c.code,
        iconUrl: flagUrl(c.countryCode, c.code),
        iconAlt: `${c.code} flag`,
      })),
      value: this._currency,
      ariaLabel: 'Currency',
      onChange: (newValue) => {
        const oldCurrency = this._currency;
        this._currency = newValue;
        this.dispatchEvent(
          createKhaimeEvent('khaime:currency-change', {
            previousCurrency: oldCurrency,
            newCurrency: this._currency,
            merchantId: this.merchantId,
          })
        );
        this.refreshProducts();
        this._cartManager?.setCurrency(this._currency);
      },
    });
  }

  /**
   * Render product type filter.
   * Hides types that aren't surfaced in the storefront filter, and turns
   * snake_case enum values into human-readable labels.
   */
  private renderTypeFilter(): void {
    const host = this.shadow.querySelector('.ks-type-filter') as HTMLElement;
    if (!host || !this._bootstrap) return;

    const HIDDEN_TYPES = new Set(['live_training', 'self_pace_training']);

    const formatLabel = (t: string): string =>
      t
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    const visibleTypes = this._bootstrap.productTypes.filter((t) => !HIDDEN_TYPES.has(t));
    const options: Array<{ value: string; label: string }> = [
      { value: '', label: 'All' },
      ...visibleTypes.map((t) => ({ value: t, label: formatLabel(t) })),
    ];

    mountCustomSelect(host, {
      options,
      value: this.getAttribute('type') || '',
      ariaLabel: 'Product type',
      onChange: (newValue) => {
        if (newValue) {
          this.setAttribute('type', newValue);
        } else {
          this.removeAttribute('type');
        }
        this._currentPage = 1;
        this.refreshProducts();
      },
    });
  }

  /**
   * Render category tabs
   */
  private renderCategories(): void {
    const container = this.shadow.querySelector('.ks-categories');
    if (!container) return;

    // For demo, use static categories - in production, fetch from API
    const categories = ['All', 'Digital', 'Physical', 'Services'];

    container.innerHTML = categories
      .map((cat) => `
        <button
          class="ks-category-btn ${cat === this._selectedCategory ? 'active' : ''}"
          data-category="${cat}"
        >${cat}</button>
      `)
      .join('');

    container.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.ks-category-btn') as HTMLElement;
      if (!btn) return;

      const category = btn.dataset.category || 'All';
      this._selectedCategory = category;

      // Update active state
      container.querySelectorAll('.ks-category-btn').forEach((b) => {
        b.classList.toggle('active', b === btn);
      });

      // Filter products
      if (category === 'All') {
        this.removeAttribute('category');
      } else {
        this.setAttribute('category', category.toLowerCase());
      }
      this._currentPage = 1;
      this.refreshProducts();
    });
  }

  /**
   * Create a product card element with all event handlers wired up
   */
  private createProductCard(product: Product): KhaimeProductCardElement {
    const card = document.createElement('khaime-product-card') as KhaimeProductCardElement;
    card.setAttribute('merchant-id', this.merchantId);
    card.setConfig(this.config);
    card.product = product;
    card.currency = this._currency;

    // Set layout attribute for list view
    if (this.config.layout.style === 'list') {
      card.setAttribute('layout', 'list');
    }

    // Forward events and handle actions
    card.addEventListener('khaime:product-click', (e) => {
      const detail = (e as CustomEvent).detail;
      this.dispatchEvent(new CustomEvent('khaime:product-click', {
        detail,
        bubbles: true,
        composed: true,
      }));
      // Open product detail panel
      this.openProductDetail(detail.product);
    });

    card.addEventListener('khaime:add-to-cart', (e) => {
      const detail = (e as CustomEvent).detail;
      const p = detail.product as Product;

      // If the product has variants and none was selected, open the detail
      // panel so the user can pick a variant first.
      if (p.variants && p.variants.length > 0 && !detail.variant) {
        this.openProductDetail(p);
        return;
      }

      // If the product has subscription options, open detail panel for frequency selection
      const subOpts = p.subscription_options;
      if (subOpts?.subscription_available && subOpts.pricing_options?.length > 0) {
        this.openProductDetail(p);
        return;
      }

      this.dispatchEvent(new CustomEvent('khaime:add-to-cart', {
        detail,
        bubbles: true,
        composed: true,
      }));
      // Add to cart manager (physical products only)
      if (this._cartManager) {
        this._cartManager.addItem(detail.product, detail.quantity, detail.variant);
      }
    });

    card.addEventListener('khaime:buy-now', (e) => {
      const detail = (e as CustomEvent).detail;
      const p = detail.product as Product;

      // If the product has variants and none was selected, open the detail
      // panel so the user can pick a variant first.
      if (p.variants && p.variants.length > 0 && !detail.variant) {
        this.openProductDetail(p);
        return;
      }

      // If the product has subscription options, open detail panel for frequency selection
      const buySubOpts = p.subscription_options;
      if (buySubOpts?.subscription_available && buySubOpts.pricing_options?.length > 0) {
        this.openProductDetail(p);
        return;
      }

      // Live events: open detail panel for ticket selection
      if (p.type === 'live_event') {
        this.openProductDetail(p);
        return;
      }

      this.dispatchEvent(new CustomEvent('khaime:buy-now', {
        detail,
        bubbles: true,
        composed: true,
      }));
      // Open direct checkout for non-physical products
      this.openDirectCheckout(detail.product, detail.quantity, detail.variant);
    });

    return card;
  }

  /**
   * Render product grid (replaces all cards)
   */
  private renderProducts(): void {
    const grid = this.shadow.querySelector('.ks-grid');
    const empty = this.shadow.querySelector('.ks-empty');
    if (!grid) return;

    if (this._products.length === 0) {
      grid.innerHTML = '';
      empty?.removeAttribute('hidden');
      return;
    }

    empty?.setAttribute('hidden', '');

    // Set grid columns
    grid.setAttribute('style', `--ks-columns: ${this.config.layout.columns}`);

    // Use layout style (grid or list)
    if (this.config.layout.style === 'list') {
      grid.classList.remove('ks-grid');
      grid.classList.add('ks-list');
    } else {
      grid.classList.remove('ks-list');
      grid.classList.add('ks-grid');
    }

    // Create product cards
    grid.innerHTML = '';
    const fragment = document.createDocumentFragment();
    this._products.forEach((product) => fragment.appendChild(this.createProductCard(product)));
    grid.appendChild(fragment);
  }

  /**
   * Append product cards to the existing grid (used by Load More)
   */
  private appendProducts(products: Product[]): void {
    const grid = this.shadow.querySelector('.ks-grid');
    if (!grid) return;

    const fragment = document.createDocumentFragment();
    products.forEach((product) => fragment.appendChild(this.createProductCard(product)));
    grid.appendChild(fragment);
  }

  /**
   * Render "Load More" button (replaces numbered pagination)
   */
  private renderPagination(): void {
    const container = this.shadow.querySelector('.ks-pagination');
    if (!container) return;

    if (!this._hasMore) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = `
      <div class="ks-load-more">
        <button class="ks-load-more-btn">
          Load More
        </button>
      </div>
    `;

    const btn = container.querySelector('.ks-load-more-btn') as HTMLButtonElement;
    btn.addEventListener('click', () => this.loadMore());
  }

  /**
   * Load more products and append them to the grid
   */
  private async loadMore(): Promise<void> {
    if (!this.api || this._loadingMore || !this._hasMore) return;

    this._loadingMore = true;
    this._currentPage++;

    // Show loading state on button
    const btn = this.shadow.querySelector('.ks-load-more-btn') as HTMLButtonElement;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = `
        <div class="ks-spinner" style="width:18px;height:18px;margin:0 auto;"></div>
      `;
    }

    try {
      const response = await this.api.getProducts(
        this.buildProductsQuery({
          limit: this._limit,
          offset: (this._currentPage - 1) * this._limit,
        }),
        { skipCache: true }
      );

      const newProducts = response.products;
      this._products = [...this._products, ...newProducts];
      this._totalProducts = response.total;
      this._hasMore = response.hasMore;

      // Append new cards to existing grid
      this.appendProducts(newProducts);

      // Update the load more button
      this.renderPagination();
    } catch (error) {
      console.error('[Khaime Storefront]', error);
      // Revert page on failure
      this._currentPage--;
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Load More';
      }
    } finally {
      this._loadingMore = false;
    }
  }

  /**
   * Show empty state
   */
  private showEmpty(): void {
    const grid = this.shadow.querySelector('.ks-grid');
    const empty = this.shadow.querySelector('.ks-empty');
    if (grid) grid.innerHTML = '';
    empty?.removeAttribute('hidden');
  }

  /**
   * Refresh products (resets to page 1, used by filter/currency/type changes)
   */
  private async refreshProducts(): Promise<void> {
    if (!this.api) return;

    // Reset to page 1 on filter changes
    this._currentPage = 1;

    // Show skeleton loading state while fetching
    const grid = this.shadow.querySelector('.ks-grid');
    if (grid) {
      const skeletonCount = Math.min(this._limit, 8);
      grid.innerHTML = this.renderSkeletonCards(skeletonCount);
    }

    try {
      // Skip client-side cache on explicit refresh (e.g. currency/filter change)
      // so the UI always reflects fresh data from the API.
      const response = await this.api.getProducts(
        this.buildProductsQuery({ limit: this._limit, offset: 0 }),
        { skipCache: true }
      );

      this._products = response.products;
      this._totalProducts = response.total;
      this._hasMore = response.hasMore;

      this.renderProducts();
      this.renderPagination();

    } catch (error) {
      console.error('[Khaime Storefront]', error);
    }
  }

  /**
   * Helper methods for conditional rendering
   */
  private shouldShowCurrency(): boolean {
    return !this.hasAttribute('hide-currency');
  }

  private shouldShowCategories(): boolean {
    return this.hasAttribute('show-categories');
  }

  private shouldShowPagination(): boolean {
    return !this.hasAttribute('hide-pagination');
  }

  /**
   * Public API: Set currency
   */
  public setCurrency(currency: string): void {
    const oldCurrency = this._currency;
    this._currency = currency;

    // Re-mount the custom select so its visible label + aria-selected
    // reflect the new value. Cheaper than hand-reconciling state.
    this.renderCurrencySelect();

    this.dispatchEvent(
      createKhaimeEvent('khaime:currency-change', {
        previousCurrency: oldCurrency,
        newCurrency: currency,
        merchantId: this.merchantId,
      })
    );

    this.refreshProducts();
  }

  /**
   * Public API: Refresh data
   */
  public override async refresh(): Promise<void> {
    this._currentPage = 1;
    await this.loadData();
  }

  /**
   * Public API: Get current products
   */
  public getProducts(): Product[] {
    return [...this._products];
  }

  /**
   * Public API: Get CartManager instance
   */
  public getCartManager(): CartManager | null {
    return this._cartManager;
  }

  /**
   * Render the floating cart icon
   */
  private renderCartIcon(): void {
    if (!this._cartManager || !this.config.features.quickBuy) return;

    // Destroy existing icon
    this._cartIconDestroy?.();

    const { destroy } = createCartIcon({
      shadowRoot: this.shadow,
      cartManager: this._cartManager,
      onClick: () => this.openCart(),
    });

    this._cartIconDestroy = destroy;
  }

  /**
   * Open product detail panel
   */
  public async openProductDetail(product: Product): Promise<void> {
    if (!this.api || !this._cartManager) return;

    // Close existing panel
    this._detailPanelCleanup?.();

    // Lazy-load the panel module
    const { openProductDetailPanel } = await import('./product-detail-panel');

    this._detailPanelCleanup = openProductDetailPanel({
      shadowRoot: this.shadow,
      api: this.api,
      cartManager: this._cartManager,
      product,
      currency: this._currency,
      config: this.config,
      merchantId: this.merchantId,
      onClose: () => {
        this._detailPanelCleanup = null;
      },
      onAddToCart: (p, qty, variant) => {
        this.dispatchEvent(new CustomEvent('khaime:add-to-cart', {
          detail: { product: p, quantity: qty, variant, merchantId: this.merchantId, timestamp: Date.now() },
          bubbles: true,
          composed: true,
        }));
      },
      onBuyNow: (p, qty, variant) => {
        this.dispatchEvent(new CustomEvent('khaime:buy-now', {
          detail: { product: p, quantity: qty, variant, merchantId: this.merchantId, timestamp: Date.now() },
          bubbles: true,
          composed: true,
        }));
        this.openDirectCheckout(p, qty, variant);
      },
    });
  }

  /**
   * Open the cart. Behavior depends on the `cart-display` attribute:
   *  - `drawer` (default): slide-out drawer; checkout CTA opens the modal.
   *  - `page`: navigate straight to the configured cart URL.
   *  - `both`: drawer opens, but its checkout CTA navigates to the cart URL
   *    so merchants can use the page as a pre-checkout review step.
   */
  public async openCart(): Promise<void> {
    if (!this._cartManager) return;

    if (this.cartDisplay === 'page') {
      window.location.assign(this.cartUrl);
      return;
    }

    // Close existing drawer
    this._drawerCleanup?.();

    // Lazy-load the drawer module
    const { openCartDrawer } = await import('../cart/cart-drawer');

    const routeToPage = this.cartDisplay === 'both';

    this._drawerCleanup = openCartDrawer({
      shadowRoot: this.shadow,
      cartManager: this._cartManager,
      currency: this._currency,
      merchantId: this.merchantId,
      onCheckout: () => {
        // Fire the checkout-init event for any listeners
        this.dispatchEvent(new CustomEvent('khaime:checkout-init', {
          detail: {
            items: this._cartManager!.getItems(),
            subtotal: this._cartManager!.getSubtotalMinor(),
            currency: this._currency,
            merchantId: this.merchantId,
            timestamp: Date.now(),
          },
          bubbles: true,
          composed: true,
        }));

        if (routeToPage) {
          window.location.assign(this.cartUrl);
          return;
        }

        // Open the cart checkout modal
        this.openCartCheckout();
      },
      onClose: () => {
        this._drawerCleanup = null;
      },
    });
  }

  /**
   * Open direct checkout for non-physical products (digital, subscription, event, service)
   * Skips the cart entirely and goes straight to customer info → payment
   */
  public async openDirectCheckout(product: Product, quantity: number = 1, variant?: ProductVariant): Promise<void> {
    if (!this.api) return;

    // Lazy-load the cart checkout modal (reused in single-product mode)
    const { openCartCheckoutModal } = await import('../checkout/cart-checkout-modal');

    openCartCheckoutModal({
      api: this.api,
      merchantId: this.merchantId,
      currency: this._currency,
      singleProduct: { product, quantity, variant },
      onSuccess: (result) => {
        this.dispatchEvent(new CustomEvent('khaime:payment-success', {
          detail: {
            sessionToken: result.sessionToken,
            orderId: result.orderId,
            amount: result.amount,
            currency: result.currency,
            merchantId: this.merchantId,
            timestamp: Date.now(),
          },
          bubbles: true,
          composed: true,
        }));
      },
      onError: (error) => {
        this.dispatchEvent(new CustomEvent('khaime:payment-error', {
          detail: {
            error: error.message,
            merchantId: this.merchantId,
            timestamp: Date.now(),
          },
          bubbles: true,
          composed: true,
        }));
      },
      onClose: () => {},
    });
  }

  /**
   * Open cart checkout modal
   * Collects customer info and initiates payment flow
   */
  public async openCartCheckout(): Promise<void> {
    if (!this.api || !this._cartManager) return;

    // Check if cart is empty
    if (this._cartManager.getItemCount() === 0) {
      console.warn('[Khaime Storefront] Cannot checkout with empty cart');
      return;
    }

    // Lazy-load the cart checkout modal
    const { openCartCheckoutModal } = await import('../checkout/cart-checkout-modal');

    openCartCheckoutModal({
      api: this.api,
      cartManager: this._cartManager,
      merchantId: this.merchantId,
      currency: this._currency,
      onSuccess: (result) => {
        this.dispatchEvent(new CustomEvent('khaime:payment-success', {
          detail: {
            sessionToken: result.sessionToken,
            orderId: result.orderId,
            amount: result.amount,
            currency: result.currency,
            merchantId: this.merchantId,
            timestamp: Date.now(),
          },
          bubbles: true,
          composed: true,
        }));
      },
      onError: (error) => {
        this.dispatchEvent(new CustomEvent('khaime:payment-error', {
          detail: {
            error: error.message,
            merchantId: this.merchantId,
            timestamp: Date.now(),
          },
          bubbles: true,
          composed: true,
        }));
      },
      onClose: () => {
        // Modal closed without completing payment
      },
    });
  }
}

// Register the component
ComponentRegistry.define('khaime-storefront', KhaimeStorefrontElement);

export default KhaimeStorefrontElement;
